/**
 * InboxGroupHeader
 *
 * A drop-in replacement for InboxGroupHeader that adds selection state support.
 * When items are selected (selectedCount > 0), it transforms into a selection bar
 * showing "n selected" and bulk action buttons. When no items are selected,
 * it behaves exactly like the original InboxGroupHeader.
 *
 * Usage:
 * - Replace InboxGroupHeader with InboxGroupHeader
 * - Add selectedCount, onBulkStar, onBulkArchive, and onClearSelection props
 * - The component will automatically switch between modes based on selectedCount
 */

import { cn } from "@/lib/utils";
import { InboxItem } from "@cohesive/api-client";
import { motion } from "framer-motion";
import { ChevronRight, CircleCheck } from "lucide-react";
import React from "react";
import { BulkActionBar } from "../BulkActionBar";

export interface InboxGroupHeaderProps {
  // Original InboxGroupHeader props
  groupId: string;
  groupName: string;
  items: InboxItem[];
  icon: React.ReactNode;
  badgeCount?: number;
  isCollapsed: boolean;
  onToggleGroup: (groupId: string) => void;
  onToggleGroupSelection?: (items: InboxItem[]) => void;
  areAllGroupItemsSelected?: (items: InboxItem[]) => boolean;

  // Selection state props
  selectedCount?: number;
  onBulkStar?: () => void;
  onBulkArchive?: () => void;
  onClearSelection?: () => void;
  selectedItemsStarred?: "all" | "none" | "mixed";
  selectedItemsArchived?: "all" | "none" | "mixed";
  className?: string;
}

export const InboxGroupHeader: React.FC<InboxGroupHeaderProps> = ({
  groupId,
  groupName,
  items,
  icon,
  badgeCount,
  isCollapsed,
  onToggleGroup,
  onToggleGroupSelection,
  areAllGroupItemsSelected,
  selectedCount = 0,
  onBulkStar,
  onBulkArchive,
  onClearSelection,
  selectedItemsStarred = "none",
  selectedItemsArchived = "none",
  className,
}) => {
  const hasSelection = selectedCount > 0;

  return (
    <div className={cn("sticky top-0 z-10 w-full", className)}>
      {hasSelection ? (
        // Selection mode - show "n selected" and bulk actions
        <button
          onClick={() => {}}
          className="w-full flex items-center justify-between py-2 h-12 transition-colors cursor-default bg-background"
        >
          <div className="flex items-center gap-2">
            <CircleCheck className="w-4 h-4 text-teal dark:text-teal" />
            <span className="text-sm font-medium text-foreground">
              {selectedCount} selected
            </span>
          </div>

          <BulkActionBar
            selectedCount={selectedCount}
            onBulkStar={onBulkStar}
            onBulkArchive={onBulkArchive}
            onClearSelection={onClearSelection}
            selectedItemsStarred={selectedItemsStarred}
            selectedItemsArchived={selectedItemsArchived}
          />
        </button>
      ) : (
        // Normal mode - show original group header
        <button
          onClick={() => onToggleGroup(groupId)}
          className="w-full flex items-center justify-between py-2 h-12 transition-colors cursor-pointer bg-background"
        >
          <div className="flex items-center gap-2">
            {icon}
            <span className="text-sm font-medium text-foreground">
              {groupName}
            </span>
            <motion.div
              animate={{ rotate: isCollapsed ? 0 : 90 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
            >
              <ChevronRight className="w-4 h-4 text-neutral-500" />
            </motion.div>
            <span
              className={cn(
                "text-xs text-muted-foreground bg-neutral-200 dark:bg-neutral-700 rounded-full px-2 py-0.5 min-w-5 text-center",
                badgeCount === 0 && "hidden",
              )}
            >
              {badgeCount || items.length}
            </span>
          </div>

          {onToggleGroupSelection && areAllGroupItemsSelected && (
            <div
              onClick={(e) => {
                e.stopPropagation();
                onToggleGroupSelection(items);
              }}
              className="text-xs text-neutral-600 dark:text-neutral-400 hover:text-white dark:hover:text-white transition-colors cursor-pointer pr-2"
              title={
                areAllGroupItemsSelected(items) ? "Deselect all" : "Select all"
              }
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  onToggleGroupSelection(items);
                }
              }}
            >
              {areAllGroupItemsSelected(items) ? "Deselect all" : "Select all"}
            </div>
          )}
        </button>
      )}
    </div>
  );
};
